import { useMemo, useState } from 'react';
import { useLocale } from '@repo/i18n';
import { getCountryFromSettings } from '@repo/common-utils/countries';
import { Iso2Code, NativeMemberRegisterRequest } from '@repo/types';
import { nativeMemberForgotPassword } from '@repo/widget-utils/services/fetchers/native-membership';
import { useBilberrySettings } from '@repo/widget-utils/services/hooks/product';
import { useConfigurations } from '@repo/widget-utils/widgetsConfiguration';
import { ApiError } from '@repo/widget-utils/services/utils/ApiError';
import { getRegisterValidationErrors, type RegisterFieldErrors } from './nativeAuthValidation';
import { useNativeMemberContext } from './NativeMemberContext';

// Smart layer for the auth modal: owns both the login and register forms, the active mode,
// tenant branding (from settings), and validation/error mapping. Auth side effects live in
// the native member context.
export function useNativeAuth() {
    const { t, locale } = useLocale();
    const settings = useBilberrySettings();
    const config = useConfigurations();
    const { login, register, isLoginModalOpen, closeLoginModal, mode, setMode } =
        useNativeMemberContext();

    // Login form
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loginError, setLoginError] = useState<string | null>(null);
    const [forgotSent, setForgotSent] = useState(false);

    // Register form
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [regEmail, setRegEmail] = useState('');
    const [regPassword, setRegPassword] = useState('');
    const [regPasswordConfirm, setRegPasswordConfirm] = useState('');
    const [phone, setPhone] = useState('');
    // Preselected from the widget locale region (falls back to Norway) so the member's country —
    // which steers the payment gateway's country-specific consent UI — is captured without friction.
    const [country, setCountry] = useState<Iso2Code>(() => getCountryFromSettings(locale));
    const [acceptTerms, setAcceptTerms] = useState(false);
    const [registerErrors, setRegisterErrors] = useState<RegisterFieldErrors>({});
    // Duplicate email is reported (not anti-enumeration), so we can steer the visitor to log in.
    const [emailExists, setEmailExists] = useState(false);

    const [submitting, setSubmitting] = useState(false);

    const { data } = settings;
    const tenant = useMemo(
        () => ({
            name: data?.name ?? '',
            logoUrl: data?.logo ?? '',
            supportEmail: data?.email ?? '',
            hasLogo: Boolean(data?.logo),
        }),
        [data],
    );

    // ---- Login ----
    const submitLogin = async () => {
        if (submitting || !email.trim() || !password) return;
        setLoginError(null);
        setForgotSent(false);
        setSubmitting(true);
        try {
            await login(email.trim(), password);
            setEmail('');
            setPassword('');
        } catch {
            // Generic message — never reveal whether the email exists.
            setLoginError(t.wrong_email_or_password);
        } finally {
            setSubmitting(false);
        }
    };

    const forgotPassword = async () => {
        setLoginError(null);
        if (!email.trim()) {
            setLoginError(t.please_provide_your_email);
            return;
        }
        // Always report success regardless of outcome (anti-enumeration).
        try {
            await nativeMemberForgotPassword(email.trim());
        } catch {
            // Intentionally indistinguishable from success.
        }
        setForgotSent(true);
    };

    // ---- Register ----
    const submitRegister = async () => {
        if (submitting) return;
        setEmailExists(false);
        const localErrors = getRegisterValidationErrors(
            { password: regPassword, passwordConfirm: regPasswordConfirm, phone },
            {
                passwordTooShort: t.password_too_short,
                passwordsDoNotMatch: t.passwords_do_not_match,
                invalidPhoneFormat: t.invalid_phone_format,
            },
        );
        if (Object.keys(localErrors).length > 0) {
            setRegisterErrors(localErrors);
            return;
        }
        setRegisterErrors({});
        setSubmitting(true);
        const payload: NativeMemberRegisterRequest = {
            // Base expects snake_case; disable the camelCase rule for this API payload.
            /* eslint-disable @typescript-eslint/naming-convention */
            email: regEmail.trim(),
            first_name: firstName.trim(),
            last_name: lastName.trim(),
            password: regPassword,
            password_confirmation: regPasswordConfirm,
            accept_terms: acceptTerms,
            // Uppercase ISO-2 to match the convention Base's admin member form stores ('NO').
            country: country.toUpperCase(),
            ...(phone.trim() ? { phone: phone.trim() } : {}),
            /* eslint-enable @typescript-eslint/naming-convention */
        };
        try {
            await register(payload);
        } catch (e) {
            if (e instanceof ApiError && e.status === 422 && e.fieldErrors) {
                const fe = e.fieldErrors;
                const next: RegisterFieldErrors = {};
                if (fe.email) {
                    next.email = t.email_already_registered;
                    setEmailExists(true);
                }
                if (fe.phone) next.phone = t.invalid_phone_format;
                if (fe.password || fe.password_confirmation) next.password = t.password_too_short;
                if (Object.keys(next).length === 0) next.general = t.registration_failed;
                setRegisterErrors(next);
            } else {
                // 404 (feature off) / 429 (rate limit) / network — keep it generic.
                setRegisterErrors({ general: t.registration_failed });
            }
        } finally {
            setSubmitting(false);
        }
    };

    // Duplicate-email path: carry the typed email over to the login tab.
    const switchToLogin = () => {
        setEmail(regEmail.trim());
        setMode('login');
    };

    return {
        mode,
        setMode,
        isOpen: isLoginModalOpen,
        close: closeLoginModal,
        tenant,
        subtitle: mode === 'signup' ? t.member_signup_subtitle : t.member_login_subtitle,
        submitting,
        loginForm: {
            email,
            setEmail,
            password,
            setPassword,
            error: loginError,
            forgotSent,
            canSubmit: !submitting && email.trim().length > 0 && password.length > 0,
            submit: submitLogin,
            forgotPassword,
        },
        registerForm: {
            firstName,
            setFirstName,
            lastName,
            setLastName,
            email: regEmail,
            setEmail: setRegEmail,
            password: regPassword,
            setPassword: setRegPassword,
            passwordConfirm: regPasswordConfirm,
            setPasswordConfirm: setRegPasswordConfirm,
            phone,
            setPhone,
            country,
            setCountry,
            acceptTerms,
            setAcceptTerms,
            errors: registerErrors,
            emailExists,
            onSwitchToLogin: switchToLogin,
            termsUrl: config.termsUrl,
            privacyUrl: config.privacyUrl,
            canSubmit:
                !submitting &&
                firstName.trim().length > 0 &&
                lastName.trim().length > 0 &&
                regEmail.trim().length > 0 &&
                regPassword.length > 0 &&
                regPasswordConfirm.length > 0 &&
                acceptTerms,
            submit: submitRegister,
        },
    };
}
